import type { CalculationMode } from '../constants.js';
import type {
  BlendAllocation,
  BlendCalculationOutcome,
  BlendResult,
  ReachabilityRange,
} from '../types.js';
import {
  decimalsForPrecision,
  gramsToUnits,
  isMeasurable,
  nearestMeasurableWeights,
  thcMgFromGrams,
  unitsToGrams,
} from '../units.js';

/**
 * Pure, deterministic blend calculation engine.
 *
 * Core idea: work entirely in INTEGER scale units (grams / precision) so that
 * "measurable" weights are exact and free of binary floating-point drift. For
 * two strains we enumerate every measurable allocation; the design generalizes
 * to N strains via a bounded composition search (guarded to protect an older
 * machine). We never compute an exact algebraic answer and round afterward —
 * every ranked candidate is itself measurable.
 */

/** Tolerance for comparing THC milligram values (well below display precision). */
const MG_EPSILON = 1e-6;

/** Safeguard: refuse combinatorial searches that would be too large. */
export const MAX_COMBINATIONS = 2_000_000;

export class BlendSearchTooLargeError extends Error {
  constructor(estimated: number) {
    super(
      `The combinatorial search is too large (${estimated} combinations). ` +
        'Multi-strain optimization for this many strains/precision is not ' +
        'supported yet.',
    );
    this.name = 'BlendSearchTooLargeError';
  }
}

export interface BlendStrain {
  strainId?: string | undefined;
  name: string;
  producer?: string | undefined;
  thcPercent: number;
}

export interface BlendCalculationInput {
  strains: BlendStrain[];
  totalWeightGrams: number;
  targetThcMg: number;
  scalePrecisionGrams: number;
  mode: CalculationMode;
  rangeMinMg?: number | undefined;
  rangeMaxMg?: number | undefined;
  /** Optional hard ceiling: candidates above this are excluded from selection. */
  maxThcMg?: number | undefined;
}

/** Internal candidate produced during enumeration. */
interface Candidate {
  units: number[];
  thcMg: number;
  signedDiff: number;
  absDiff: number;
  /** Balance metric: max(units) - min(units); smaller = more balanced. */
  balance: number;
}

/** Number of weak compositions of `total` into `k` non-negative parts. */
function compositionCount(k: number, total: number): number {
  // C(total + k - 1, k - 1)
  const n = total + k - 1;
  const r = Math.min(k - 1, total);
  let result = 1;
  for (let i = 1; i <= r; i++) {
    result = (result * (n - r + i)) / i;
    if (result > Number.MAX_SAFE_INTEGER) return Number.POSITIVE_INFINITY;
  }
  return Math.round(result);
}

/**
 * Evaluate a single allocation (in units) into a candidate. THC is computed
 * from the measurable gram value so it is consistent with what is displayed,
 * while full numeric precision is retained internally.
 */
function evaluate(
  units: number[],
  strains: BlendStrain[],
  precision: number,
  targetThcMg: number,
): Candidate {
  let thcMg = 0;
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < units.length; i++) {
    const u = units[i] ?? 0;
    const grams = unitsToGrams(u, precision);
    thcMg += thcMgFromGrams(grams, strains[i]!.thcPercent);
    if (u < min) min = u;
    if (u > max) max = u;
  }
  const signedDiff = thcMg - targetThcMg;
  return {
    units,
    thcMg,
    signedDiff,
    absDiff: Math.abs(signedDiff),
    balance: max - min,
  };
}

/** Lexicographic comparison of two unit arrays (descending preference). */
function lexCompareDesc(a: number[], b: number[]): number {
  for (let i = 0; i < a.length; i++) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av !== bv) return bv - av; // larger first-strain amount ranks first
  }
  return 0;
}

/**
 * "Closest" comparator implementing the ranking rules:
 * 1) smallest absolute difference
 * 2) prefer at/below target on ties
 * 3) prefer the more balanced blend
 * 4) deterministic lexicographic order
 * Returns negative if `a` is preferred over `b`.
 */
function compareClosest(a: Candidate, b: Candidate): number {
  if (Math.abs(a.absDiff - b.absDiff) > MG_EPSILON) return a.absDiff - b.absDiff;
  const aBelow = a.signedDiff <= MG_EPSILON;
  const bBelow = b.signedDiff <= MG_EPSILON;
  if (aBelow !== bBelow) return aBelow ? -1 : 1;
  if (a.balance !== b.balance) return a.balance - b.balance;
  return lexCompareDesc(a.units, b.units);
}

/** Compare by |diff| then balance then deterministic order (no sign preference). */
function compareAbsThenBalance(a: Candidate, b: Candidate): number {
  if (Math.abs(a.absDiff - b.absDiff) > MG_EPSILON) return a.absDiff - b.absDiff;
  if (a.balance !== b.balance) return a.balance - b.balance;
  return lexCompareDesc(a.units, b.units);
}

/** Compare by distance to a midpoint value, then balance, then determinism. */
function compareToMid(a: Candidate, b: Candidate, mid: number): number {
  const da = Math.abs(a.thcMg - mid);
  const db = Math.abs(b.thcMg - mid);
  if (Math.abs(da - db) > MG_EPSILON) return da - db;
  if (a.balance !== b.balance) return a.balance - b.balance;
  return lexCompareDesc(a.units, b.units);
}

function pick(
  current: Candidate | null,
  next: Candidate,
  cmp: (a: Candidate, b: Candidate) => number,
): Candidate {
  if (current === null) return next;
  return cmp(next, current) < 0 ? next : current;
}

interface Accumulator {
  /** Closest overall, ignoring any max-THC cap (used as ultimate fallback). */
  overall: Candidate | null;
  best: Candidate | null;
  below: Candidate | null;
  above: Candidate | null;
  inRange: Candidate | null;
}

/**
 * Enumerate all measurable allocations, updating running "best" candidates for
 * every mode in a single pass (memory-light: we never store the full list).
 */
function search(
  strains: BlendStrain[],
  totalUnits: number,
  precision: number,
  targetThcMg: number,
  rangeMinMg: number | undefined,
  rangeMaxMg: number | undefined,
  maxThcMg: number | undefined,
): Accumulator {
  const k = strains.length;
  const count = compositionCount(k, totalUnits);
  if (count > MAX_COMBINATIONS) throw new BlendSearchTooLargeError(count);

  const mid =
    rangeMinMg !== undefined && rangeMaxMg !== undefined
      ? (rangeMinMg + rangeMaxMg) / 2
      : targetThcMg;

  const acc: Accumulator = {
    overall: null,
    best: null,
    below: null,
    above: null,
    inRange: null,
  };

  const consider = (units: number[]): void => {
    const c = evaluate(units, strains, precision, targetThcMg);
    // `overall` ignores the cap so we always have a fallback to show.
    acc.overall = pick(acc.overall, c, compareClosest);

    // A max-THC ceiling (if provided) excludes candidates above it.
    if (maxThcMg !== undefined && c.thcMg > maxThcMg + MG_EPSILON) return;

    acc.best = pick(acc.best, c, compareClosest);
    if (c.signedDiff <= MG_EPSILON) {
      acc.below = pick(acc.below, c, compareAbsThenBalance);
    }
    if (c.signedDiff >= -MG_EPSILON) {
      acc.above = pick(acc.above, c, compareAbsThenBalance);
    }
    if (
      rangeMinMg !== undefined &&
      rangeMaxMg !== undefined &&
      c.thcMg >= rangeMinMg - MG_EPSILON &&
      c.thcMg <= rangeMaxMg + MG_EPSILON
    ) {
      acc.inRange = pick(acc.inRange, c, (x, y) => compareToMid(x, y, mid));
    }
  };

  // Recursive composition generation with an in-place buffer.
  const buffer = new Array<number>(k).fill(0);
  const recurse = (index: number, remaining: number): void => {
    if (index === k - 1) {
      buffer[index] = remaining;
      consider(buffer.slice());
      return;
    }
    for (let x = 0; x <= remaining; x++) {
      buffer[index] = x;
      recurse(index + 1, remaining - x);
    }
  };
  recurse(0, totalUnits);

  return acc;
}

function computeReachability(
  strains: BlendStrain[],
  totalUnits: number,
  precision: number,
): ReachabilityRange {
  const totalGrams = unitsToGrams(totalUnits, precision);
  let minIdx = 0;
  let maxIdx = 0;
  for (let i = 1; i < strains.length; i++) {
    if (strains[i]!.thcPercent < strains[minIdx]!.thcPercent) minIdx = i;
    if (strains[i]!.thcPercent > strains[maxIdx]!.thcPercent) maxIdx = i;
  }
  return {
    minThcMg: thcMgFromGrams(totalGrams, strains[minIdx]!.thcPercent),
    maxThcMg: thcMgFromGrams(totalGrams, strains[maxIdx]!.thcPercent),
    minStrainIndex: minIdx,
    maxStrainIndex: maxIdx,
  };
}

function toResult(
  candidate: Candidate,
  strains: BlendStrain[],
  precision: number,
  totalUnits: number,
  targetThcMg: number,
  mode: CalculationMode,
  reachability: ReachabilityRange,
): BlendResult {
  const decimals = decimalsForPrecision(precision);
  const allocations: BlendAllocation[] = candidate.units.map((u, i) => {
    const grams = unitsToGrams(u, precision);
    const s = strains[i]!;
    return {
      strainIndex: i,
      strainId: s.strainId,
      name: s.name,
      producer: s.producer,
      thcPercent: s.thcPercent,
      units: u,
      grams,
      thcMg: thcMgFromGrams(grams, s.thcPercent),
    };
  });

  const estimatedThcMg = candidate.thcMg;
  const differenceMg = estimatedThcMg - targetThcMg;
  return {
    allocations,
    totalWeightGrams: unitsToGrams(totalUnits, precision),
    totalUnits,
    scalePrecisionGrams: precision,
    displayDecimals: decimals,
    estimatedThcMg,
    targetThcMg,
    differenceMg,
    absoluteDifferenceMg: Math.abs(differenceMg),
    percentDifference:
      targetThcMg > 0 ? (differenceMg / targetThcMg) * 100 : null,
    mode,
    exactlyAchievable: Math.abs(differenceMg) <= MG_EPSILON,
    reachability,
  };
}

/**
 * Calculate the closest achievable, measurable blend for the requested inputs.
 * Returns a full outcome including weight-measurability problems, reachability,
 * and — when a restrictive mode cannot be satisfied — the closest lower/higher
 * fallbacks.
 */
export function calculateBlend(
  input: BlendCalculationInput,
): BlendCalculationOutcome {
  const {
    strains,
    totalWeightGrams,
    targetThcMg,
    scalePrecisionGrams: precision,
    mode,
    rangeMinMg,
    rangeMaxMg,
    maxThcMg,
  } = input;

  // 1) Validate the total weight is measurable at the given precision.
  if (!isMeasurable(totalWeightGrams, precision)) {
    const { lower, higher } = nearestMeasurableWeights(
      totalWeightGrams,
      precision,
    );
    return {
      result: null,
      satisfiedMode: false,
      targetOutOfRange: false,
      weightProblem: {
        message: `A target of ${totalWeightGrams} g cannot be measured exactly on a ${precision} g scale.`,
        requestedWeightGrams: totalWeightGrams,
        nearestLowerGrams: lower > 0 ? lower : null,
        nearestHigherGrams: higher,
      },
    };
  }

  const totalUnits = gramsToUnits(totalWeightGrams, precision);
  const reachability = computeReachability(strains, totalUnits, precision);
  const targetOutOfRange =
    targetThcMg < reachability.minThcMg - MG_EPSILON ||
    targetThcMg > reachability.maxThcMg + MG_EPSILON;

  const acc = search(
    strains,
    totalUnits,
    precision,
    targetThcMg,
    rangeMinMg,
    rangeMaxMg,
    maxThcMg,
  );

  const build = (
    c: Candidate,
    m: CalculationMode = mode,
  ): BlendResult =>
    toResult(c, strains, precision, totalUnits, targetThcMg, m, reachability);

  const fallback = (): Pick<
    BlendCalculationOutcome,
    'closestBelow' | 'closestAbove'
  > => ({
    closestBelow: acc.below ? build(acc.below) : null,
    closestAbove: acc.above ? build(acc.above) : null,
  });

  // A max-THC ceiling below the minimum reachable THC leaves no valid
  // candidate; fall back to the closest overall and flag the mode as unmet.
  if (acc.best === null) {
    return {
      result: acc.overall ? build(acc.overall) : null,
      satisfiedMode: false,
      targetOutOfRange,
      closestBelow: null,
      closestAbove: acc.overall ? build(acc.overall) : null,
    };
  }

  // `best` exists within the (possibly capped) candidate set.
  const best = acc.best;

  switch (mode) {
    case 'closest':
      return { result: build(best), satisfiedMode: true, targetOutOfRange };

    case 'do_not_exceed':
      if (acc.below) {
        return {
          result: build(acc.below),
          satisfiedMode: true,
          targetOutOfRange,
        };
      }
      return {
        result: build(best),
        satisfiedMode: false,
        targetOutOfRange,
        ...fallback(),
      };

    case 'at_least':
      if (acc.above) {
        return {
          result: build(acc.above),
          satisfiedMode: true,
          targetOutOfRange,
        };
      }
      return {
        result: build(best),
        satisfiedMode: false,
        targetOutOfRange,
        ...fallback(),
      };

    case 'target_range':
      if (acc.inRange) {
        return {
          result: build(acc.inRange),
          satisfiedMode: true,
          targetOutOfRange,
        };
      }
      return {
        result: build(best),
        satisfiedMode: false,
        targetOutOfRange,
        ...fallback(),
      };

    default:
      return { result: build(best), satisfiedMode: true, targetOutOfRange };
  }
}
